import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { rowMatchesUnifiedSearch } from "../../shared/unifiedSearch";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import { useToast } from "../../hooks/use-toast";
import { Option } from "../../interface/Option";
import { Result, activityAgeGender } from "../../interface/ActivityAgeGender";
import { ActivityAgeGenderAxiosInstance } from "../../Network/ActivityAgeGenderAxiosInstance";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import FileUpload from "Pages/uploadFile/ActivityAgeGender/ActivityAgeGender";
import ListOfDoctorName from "../Shared/Doctor";
import Vip from "../Shared/Vip";
import ListOfSpecialty from "../Shared/Specialty";

// ─── Types ────────────────────────────────────────────────────────────────────
export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}

interface CodeWithType {
  code: string | number;
  type: string;
}

type ActivitiesByType = { CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] };
const EMPTY_BY_TYPE: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };

// ─── preserveScroll helper ────────────────────────────────────────────────────
const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => {
    if (scrollable) scrollable.scrollTop = scrollTop;
  });
};

// ─── EntityCard ───────────────────────────────────────────────────────────────
const entityColors: Record<string, { border: string; bg: string; text: string; badgeBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200" },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200" },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200" },
};

interface EntityCardProps {
  label: string;
  icon: string;
  color: string;
  isActive: boolean;
  mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void;
  onInclusion: () => void;
  onClear: () => void;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({
  label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, children,
}) => {
  const c = entityColors[color] ?? entityColors.slate;

  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => {
      if (scrollable) scrollable.scrollTop = scrollTop;
    });
  };

  return (
    <div className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}>
      {isActive && (
        <button
          type="button"
          onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10"
          title="Clear"
        >
          <i className="fa-solid fa-xmark text-[14px]"></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button
            type="button"
            onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Exclusion
          </button>
          <button
            type="button"
            onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Inclusion
          </button>
        </div>
      </div>
      {isActive && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── mode conversion helpers ──────────────────────────────────────────────────
const cardToMode = (mode: "EXCLUSION" | "INCLUSION" | null): string => {
  if (mode === "EXCLUSION") return "EXCLUDED";
  if (mode === "INCLUSION") return "INCLUDED";
  return "DISABLED";
};

const modeToCard = (mode: string | undefined): "EXCLUSION" | "INCLUSION" | null => {
  if (mode === "EXCLUDED") return "EXCLUSION";
  if (mode === "INCLUDED") return "INCLUSION";
  return null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getActivityTypeColor = (type: string) => {
  const colors: Record<string, { bg: string; border: string; text: string; hex: string }> = {
    CPT:    { bg: "bg-blue-100",   border: "border-blue-400",   text: "text-blue-900",   hex: "#1d4ed8" },
    HCPCS:  { bg: "bg-green-100",  border: "border-green-400",  text: "text-green-900",  hex: "#15803d" },
    Dental: { bg: "bg-purple-100", border: "border-purple-400", text: "text-purple-900", hex: "#7e22ce" },
    Drugs:  { bg: "bg-orange-100", border: "border-orange-400", text: "text-orange-900", hex: "#c2410c" },
  };
  return colors[type] || { bg: "bg-gray-100", border: "border-gray-400", text: "text-gray-900", hex: "#374151" };
};

const TYPE_MAP: Record<string, string> = {
  cpt: "CPT", CPT: "CPT",
  hcpcs: "HCPCS", HCPCS: "HCPCS",
  dental: "Dental", Dental: "Dental",
  drugs: "Drugs", Drugs: "Drugs",
};

const flattenByType = (byType: ActivitiesByType): CodeWithType[] => {
  const result: CodeWithType[] = [];
  Object.entries(byType).forEach(([type, options]) => {
    options.forEach((o) => result.push({ code: o.value as string, type }));
  });
  return result;
};

const groupByType = (items: CodeWithType[]): ActivitiesByType => {
  const grouped: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
  items.forEach(({ code, type }) => {
    const key = TYPE_MAP[type] as keyof ActivitiesByType;
    if (key && grouped[key]) grouped[key].push({ value: code, label: code.toString() });
  });
  return grouped;
};

const uniqueTypes = (flat: CodeWithType[]): string[] =>
  Array.from(new Set(flat.map((c) => c.type.toLowerCase())));

// ─── normalizeResult ──────────────────────────────────────────────────────────
const normalizeResult = (item: any): Result & { activity_types: CodeWithType[] } => {
  let activity_types: CodeWithType[] = [];
  if (Array.isArray(item.activity_types) && item.activity_types.length > 0 && typeof item.activity_types[0] === "object" && item.activity_types[0] !== null) {
    activity_types = item.activity_types.map((at: any) => ({
      code: at.code,
      type: TYPE_MAP[at.type?.toLowerCase()] || at.type || "CPT",
    }));
  } else if (Array.isArray(item.activity_code) && item.activity_code.length > 0 && Array.isArray(item.activity_type)) {
    activity_types = item.activity_code.map((code: string, idx: number) => {
      const rawType = Array.isArray(item.activity_type) ? item.activity_type[idx] || item.activity_type[0] : item.activity_type;
      return { code, type: TYPE_MAP[rawType?.toLowerCase()] || "CPT" };
    });
  }

  return {
    ...item,
    activity_types,
    // ── normalize field name aliases from backend ──
    network:  item.network  ?? item.networks  ?? [],
    networks: item.networks ?? item.network   ?? [],
    patient:  item.patient  ?? item.patients  ?? [],
    patients: item.patients ?? item.patient   ?? [],
  };
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const StatusToggle: React.FC<{ label: string; dotColor: string; value: boolean; onChange: (v: boolean) => void }> = ({ label, dotColor, value, onChange }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
    <div className="flex items-center gap-2 mb-2">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
      <span className="text-xs font-medium text-gray-600">{label}</span>
    </div>
    <div className="flex gap-2">
      <button type="button" onClick={() => onChange(true)} className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${value ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Active</button>
      <button type="button" onClick={() => onChange(false)} className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${!value ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Inactive</button>
    </div>
  </div>
);

const ActivityTypeCell: React.FC<{
  items: CodeWithType[]; groups?: string[];
  onShowAll: (items: CodeWithType[], groups: string[]) => void;
}> = ({ items, groups = [], onShowAll }) => {
  if (items.length === 0 && groups.length === 0)
    return <div className="w-full text-center text-gray-400">—</div>;
  if (items.length === 0) {
    const shown = groups.slice(0, 2);
    return <div className="w-full text-center text-sm text-gray-700">{shown.join(", ")}{groups.length > 2 ? "…" : ""}</div>;
  }
  const display = items.slice(0, 3);
  const hasMore = items.length > 3 || groups.length > 0;
  return (
    <div className="w-full px-2 py-1.5">
      <div className="flex flex-wrap justify-center gap-1.5 mb-1.5">
        {display.map((c, idx) => {
          const col = getActivityTypeColor(c.type);
          return (
            <div key={idx} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${col.bg} ${col.border}`}>
              <span className={`font-semibold text-xs ${col.text}`}>{c.code}</span>
              <span className={`text-[9px] font-bold uppercase ${col.text} opacity-75`}>{c.type}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between border-t border-gray-100 pt-1">
        <span className="text-xs font-bold text-gray-500">{items.length + groups.length}</span>
        {hasMore && (
          <button onClick={() => onShowAll(items, groups)} className="text-[11px] text-blue-600 hover:text-blue-800 font-semibold">View all →</button>
        )}
      </div>
    </div>
  );
};
// ─── Main Component ───────────────────────────────────────────────────────────
const ActivityAgeAndGender: React.FC = () => {
  const [items, setItems] = useState<{ result: (Result & { activity_types: CodeWithType[] })[] }>({ result: [] });

  const [gender, setGender] = useState<string>("");
  const [relationName, setRelationName] = useState<string>("");
  const [lowerAge, setLowerAge] = useState<number | null>(null);
  const [higherAge, setHigherAge] = useState<number | null>(null);
  const [ageExclude, setAgeExclude] = useState<boolean>(true);
  const [isActiveToAge, setIsActiveToAge] = useState<boolean>(true);
  const [isActiveToGender, setIsActiveToGender] = useState<boolean>(true);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [ageError, setAgeError] = useState<string>("");
  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [activitiesByType, setActivitiesByType] = useState<ActivitiesByType>({ ...EMPTY_BY_TYPE });
  const [customActivityGroups, setCustomActivityGroups] = useState<Option[]>([]);
  const [medicalActivityGroups, setMedicalActivityGroups] = useState<Option[]>([]);
  // ── Entities toggle ──
  const [showEntities, setShowEntities] = useState(false);

  // ── Provider ──
  const [provider, setProvider] = useState<Option[]>([]);
  const [providerMode, setProviderMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);

  // ── Doctor ──
  const [doctor, setDoctor] = useState<Option[]>([]);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);

  // ── Networks ──
  const [networks, setNetworks] = useState<Option[]>([]);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);

  // ── Insurance Plan ──
  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);

  // ── Payer ──
  const [Payer, setPayer] = useState<string[]>([]);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  // ── Policy ──
  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);

  // ── Patients ──
  const [patients, setPatients] = useState<Option[]>([]);
  const [patientMode, setPatientMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);

  // ── VIP ──
  const [vip, setVip] = useState<Option[]>([]);
  const [vipMode, setVipMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive] = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);

  // ── User Level ──
  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [userMode, setUserMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive] = useState(false);
  const [selectAllUser, setSelectAllUser] = useState(false);

  // ── Specialty ──
  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);

  // ── clear flags ──
  const [clearProvider, setClearProvider] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);
  const [clearVip, setClearVip] = useState(false);
  const [clearSpecialty, setClearSpecialty] = useState(false);

  // ── dialog ──
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [selectedItem, setSelectedItem] = useState<(Result & { activity_types: CodeWithType[] }) | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [allCodesOpen, setAllCodesOpen] = useState(false);
  const [allCodesItems, setAllCodesItems] = useState<CodeWithType[]>([]);
  const [allCodesGroups, setAllCodesGroups] = useState<string[]>([]);
  const [allCodesTitle, setAllCodesTitle] = useState("");
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});

  // ── filters ──
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterGender, setFilterGender] = useState<string>("");
  const [filterGenderValidation, setFilterGenderValidation] = useState<string>("");
  const [filterAgeValidation, setFilterAgeValidation] = useState<string>("");
  const [genderOpen, setGenderOpen] = useState(false);
  const [gValidOpen, setGValidOpen] = useState(false);
  const [aValidOpen, setAValidOpen] = useState(false);
  const genderRef = useRef<HTMLDivElement>(null);
  const gValidRef = useRef<HTMLDivElement>(null);
  const aValidRef = useRef<HTMLDivElement>(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!genderRef.current?.contains(e.target as Node)) setGenderOpen(false);
      if (!gValidRef.current?.contains(e.target as Node)) setGValidOpen(false);
      if (!aValidRef.current?.contains(e.target as Node)) setAValidOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { fetchUsers(); }, []);

  const validateAge = (lo: number | null, hi: number | null) => {
    if (lo !== null && hi !== null && lo > hi) { setAgeError("Lower age cannot exceed higher age."); return false; }
    setAgeError(""); return true;
  };

  // ── Reset all entity states ──────────────────────────────────────────────
  const resetAllEntityStates = () => {
    setProvider([]); setProviderMode(null); setIsProviderActive(false); setSelectAllProviders(false);
    setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setSelectAllDoctor(false);
    setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setSelectAllNetworks(false);
    setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false);
    setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setSelectAllPolicy(false);
    setPatients([]); setPatientMode(null); setIsPatientActive(false); setSelectAllPatients(false);
    setVip([]); setVipMode(null); setIsVipActive(false); setSelectAllVip(false);
    setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false);
    setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
    setClearProvider(true); setClearDoctor(true); setClearNetworks(true);
    setClearInsuranceplan(true); setClearPatient(true); setClearPolicy(true);
    setClearVip(true); setClearSpecialty(true);
  };

  const handleToggleEntities = () => {
    if (showEntities) resetAllEntityStates();
    setShowEntities((prev) => !prev);
  };


  const validateEntities = (): string | null => {
  const checks = [
    { isActive: isProviderActive,   hasData: selectAllProviders || provider.length > 0,     label: "Providers" },
    { isActive: isDoctorActive,     hasData: selectAllDoctor    || doctor.length > 0,        label: "Doctor" },
    { isActive: isNetworkActive,    hasData: selectAllNetworks  || networks.length > 0,      label: "Networks" },
    { isActive: isInsuranceActive,  hasData: selectAllInsurance || insuranceplan.length > 0, label: "Insurance Plan" },
    { isActive: isPolicyActive,     hasData: selectAllPolicy    || policy.length > 0,        label: "Policy" },
    { isActive: isPatientActive,    hasData: selectAllPatients  || patients.length > 0,      label: "Patients" },
    { isActive: isVipActive,        hasData: selectAllVip       || vip.length > 0,           label: "VIP" },
    { isActive: isSpecialtyActive,  hasData: selectAllSpecialty || specialty.length > 0,     label: "Specialty" },
    { isActive: isPayerActive,      hasData: selectAllPayer     || Payer.length > 0,         label: "Payer" },
    { isActive: isUserActive,       hasData: selectAllUser      || userLevel.length > 0,     label: "User Level" },
  ];

  const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);

  if (empty.length >= 1) {
    return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the ✕ button.`;
  }
  return null;
};
  const clear = () => {
    setGender(""); setRelationName("");
    setActivitiesByType({ ...EMPTY_BY_TYPE });
    setCustomActivityGroups([]); setMedicalActivityGroups([]);
    setLowerAge(null); setHigherAge(null);
    setAgeExclude(true); setIsActiveToAge(true); setIsActiveToGender(true);
    setIsActive(true); setAgeError("");
    setSelectedMainOption(null);
    setShowEntities(false);
    resetAllEntityStates();
  };

  const buildProviderValues = (arr: Option[]) =>
    arr.map((o: any) => {
      if (o.providerId && o.providerName) return { Provider_id: o.providerId, Provider_name: o.providerName };
      const match = o.label?.match(/^\((.+)\) (.+)$/);
      if (match) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
      return { Provider_id: o.value?.toString() || "", Provider_name: o.label?.toString() || "" };
    });

  const buildPolicyValues = (arr: PolicyItem[]) =>
    arr.map((p: any) => {
      if (typeof p === "object" && p.Policy_id && !p.value) return { id: p.id || 0, Policy_id: p.Policy_id, Policy_name: p.Policy_name, plans: p.plans || [] };
      if (typeof p === "object" && p.value) return { id: p.value.id || 0, Policy_id: p.value.Policy_id || "", Policy_name: p.value.Policy_name || "", plans: p.value.plans || [] };
      if (typeof p === "string") return { id: 0, Policy_id: p, Policy_name: p, plans: [] };
      return { id: 0, Policy_id: "", Policy_name: "", plans: [] };
    });


  const {
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  totalPages,
  totalCount,
  allowedPageSizes,
  isTableLoading,
  manualPagination,
  fetchItems,
} = useServerPaginatedList<
  Result & { activity_types: CodeWithType[] },
  {
    search: string;
    gender: string;
    genderValidation: string;
    ageValidation: string;
    deleted: ServerDeletedFilterValue;
  }
>({
  
    filters: { search: filterSearch, gender: filterGender, genderValidation: filterGenderValidation, ageValidation: filterAgeValidation, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      ActivityAgeGenderAxiosInstance.get(
        "getActivityagetogender",
        {
          params: {
            page,
            limit,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ result: rows }),
    normalizeRow: normalizeResult,
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    isClientFallbackActive: (filters) =>
      filters.search.trim().length >= 2 || Boolean(filters.gender || filters.genderValidation || filters.ageValidation),
    clientFilterRows: (rows, filters) => rows.filter((item) => {
      if (filters.gender && item.gender?.toLowerCase() !== filters.gender) return false;
      if (filters.genderValidation === "true" && item.is_active_to_gender !== true) return false;
      if (filters.genderValidation === "false" && item.is_active_to_gender !== false) return false;
      if (filters.ageValidation === "true" && item.is_active_to_age !== true) return false;
      if (filters.ageValidation === "false" && item.is_active_to_age !== false) return false;
      return rowMatchesUnifiedSearch(item, filters.search);
    }),
    onError: (error) => {
      console.error("Failed to fetch Activity Age And Gender", error);
      toast({
        title: "Error",
        description: "Failed to fetch Activity Age And Gender",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };


  const handleOpenDialog = (item: Result & { activity_types: CodeWithType[] }) => { setSelectedItem(item); setViewDialogOpen(true); };
const handleCloseDialog = () => { setViewDialogOpen(false); setSelectedItem(null); setShowAllUpdates(false); };
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
  const handleEditClick = (item: any) => { handleEdit(item); setIsDialogOpen(true); };

  // ── build payload ─────────────────────────────────────────────────────────
  const buildPayload = () => {
    const flat = flattenByType(activitiesByType);
    return {
      gender,
      relation_name: relationName,
      activity_type: uniqueTypes(flat),
      activity_code: flat.map((c) => c.code),
      activity_types: flat.map((c) => ({ code: c.code, type: c.type })),
      custom_activity_groups: customActivityGroups.map((o) => o.value),
      medical_activity_groups: medicalActivityGroups.map((o) => o.value),
      lower_age: lowerAge,
      higher_age: higherAge,
      age_exclude: ageExclude,
      age_allowed: !ageExclude,
      is_active_to_age: isActiveToAge,
      is_active_to_gender: isActiveToGender,
      // ── entities ──
      provider: buildProviderValues(provider),
      provider_mode: cardToMode(providerMode),
      provider_all: selectAllProviders,
      network: networks.map((o) => o.value),
      network_mode: cardToMode(networkMode),
      network_all: selectAllNetworks,
      insuranceplan: insuranceplan.map((o) => o.value),
      insuranceplan_mode: cardToMode(insuranceMode),
      insuranceplan_all: selectAllInsurance,
      payer: Payer,
      payer_mode: cardToMode(payerMode),
      payer_all: selectAllPayer,
      patient: patients.map((o) => o.label),
      patient_mode: cardToMode(patientMode),
      patient_all: selectAllPatients,
      policy: buildPolicyValues(policy),
      policy_mode: cardToMode(policyMode),
      policy_all: selectAllPolicy,
      user_level: userLevel,
      user_mode: cardToMode(userMode),
      user_all: selectAllUser,
      vip: vip.map((o) => o.value),
      vip_mode: cardToMode(vipMode),
      vip_all: selectAllVip,
      doctor: doctor.map((o) => ({ Doctor_id: o.value, Doctor_name: o.label })),
      doctor_mode: cardToMode(doctorMode),
      doctor_all: selectAllDoctor,
      specialty: specialty.map((o) => o.value),
      specialty_mode: cardToMode(specialtyMode),
      specialty_all: selectAllSpecialty,
      is_active: isActive,
    };
  };

  const addItem = async (): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }

    if (!isActiveToGender && !isActiveToAge) {
      toast({
        title: "Error",
        description: "At least one of Gender Status or Age Status must be Active. You cannot create a rule with both inactive.",
        variant: "destructive",
      });
      return;
    }

    if (isActiveToGender && !gender) {
      toast({
        title: "Error",
        description: "Gender Status is active — please choose a gender (Male or Female). No default is allowed.",
        variant: "destructive",
      });
      return;
    }

    if (isActiveToAge && (lowerAge == null || higherAge == null)) {
      toast({
        title: "Error",
        description: "Age Status is active — please enter both a lower and a higher age.",
        variant: "destructive",
      });
      return;
    }

    if (!validateAge(lowerAge, higherAge)) { toast({ title: "Error", description: "Lower age cannot exceed higher age.", variant: "destructive" }); return; }
    const entityError = validateEntities();
if (entityError) {
  toast({ title: "Validation Error", description: entityError, variant: "destructive" });
  return;
}
    try {
      const response = await ActivityAgeGenderAxiosInstance.post("addnewActivityagetogender", { ...buildPayload(), token });
     if (response.data.success) {
  await fetchItems();
  setCurrentPage(0);
  clear(); setIsDialogOpen(false);
  toast({ title: "Success", description: "Rule added successfully." });
}
 else {
        const errors = response.data.errors || [];
        toast({ title: "Error", description: errors.map((e: any) => e.error).join("\n") || response.data.message || "Failed to add.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!isActiveToGender && !isActiveToAge) {
      toast({
        title: "Error",
        description: "At least one of Gender Status or Age Status must be Active. You cannot create a rule with both inactive.",
        variant: "destructive",
      });
      return;
    }

    if (isActiveToGender && !gender) {
      toast({
        title: "Error",
        description: "Gender Status is active — please choose a gender (Male or Female). No default is allowed.",
        variant: "destructive",
      });
      return;
    }

    if (isActiveToAge && (lowerAge == null || higherAge == null)) {
      toast({
        title: "Error",
        description: "Age Status is active — please enter both a lower and a higher age.",
        variant: "destructive",
      });
      return;
    }

    if (!validateAge(lowerAge, higherAge)) { toast({ title: "Error", description: "Lower age cannot exceed higher age.", variant: "destructive" }); return; }
    const entityError = validateEntities();
if (entityError) {
  toast({ title: "Validation Error", description: entityError, variant: "destructive" });
  return;
}
    try {
      const response = await ActivityAgeGenderAxiosInstance.put("updateActivityagetogender", { ...buildPayload(), id, token });
      if (response.data.success) {
       await fetchItems();
  setCurrentPage(0);
  clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Rule updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };
  // ── handleEdit ────────────────────────────────────────────────────────────
  const handleEdit = (item: any) => {
    const normalized = normalizeResult(item);

    setRelationName(normalized.relation_name);
    setGender(normalized.gender || "");
    setLowerAge(normalized.lower_age);
    setHigherAge(normalized.higher_age);
    setAgeExclude(normalized.age_allowed !== undefined ? !normalized.age_allowed : normalized.age_exclude);
    setIsActive(normalized.is_active);
    setIsActiveToAge(normalized.is_active_to_age ?? true);
    setIsActiveToGender(normalized.is_active_to_gender ?? true);

    // Activity
    const flat: CodeWithType[] = normalized.activity_types || [];
    if (flat.length > 0) { setActivitiesByType(groupByType(flat)); setSelectedMainOption("list_of_Activity_code"); }
    else { setActivitiesByType({ ...EMPTY_BY_TYPE }); }

    const customData = Array.isArray(normalized.custom_activity_groups)
      ? normalized.custom_activity_groups.map((o: any) => typeof o === "string" ? { id: 0, name: o } : o)
      : [];
    setCustomActivityGroups(customData.map((o: any) => ({ value: o.id, label: o.name })));
    if (customData.length > 0 && flat.length === 0) setSelectedMainOption("custom_Activity_groups");

    const medicalData = Array.isArray(normalized.medical_activity_groups)
      ? normalized.medical_activity_groups.map((o: any) => typeof o === "string" ? { id: 0, name: o } : o)
      : [];
    setMedicalActivityGroups(medicalData.map((o: any) => ({ value: o.id, label: o.name })));
    if (medicalData.length > 0 && flat.length === 0 && customData.length === 0) setSelectedMainOption("medical_Activity_groups");
    if (flat.length > 0) setSelectedMainOption("list_of_Activity_code");

    // Provider
    const mappedProviders = (Array.isArray(normalized.provider)
      ? normalized.provider.map((o: any) => typeof o === "string" ? { Provider_id: 0, Provider_name: o } : o)
      : []).map((o: any) => ({ value: o.Provider_id, label: o.Provider_name }));
    setProvider(mappedProviders);
    const pMode = modeToCard(normalized.provider_mode);
    setProviderMode(pMode);
    setIsProviderActive(pMode !== null || mappedProviders.length > 0 || normalized.provider_all);
    setSelectAllProviders(normalized.provider_all ?? false);

    // Doctor
    const mappedDoctors = (Array.isArray(normalized.doctor)
      ? normalized.doctor.map((o: any) => typeof o === "string" ? { Doctor_id: 0, Doctor_name: o } : o)
      : []).map((o: any) => ({ value: o.Doctor_id, label: o.Doctor_name }));
    setDoctor(mappedDoctors);
    const dMode = modeToCard(normalized.doctor_mode);
    setDoctorMode(dMode);
    setIsDoctorActive(dMode !== null || mappedDoctors.length > 0 || normalized.doctor_all);
    setSelectAllDoctor(normalized.doctor_all ?? false);

    // Networks — backend returns "network"
    const networkSource = normalized.network ?? normalized.networks ?? [];
    const mappedNetworks = (Array.isArray(networkSource)
      ? networkSource.map((o: any) => typeof o === "string" ? { id: 0, network_name: o } : o)
      : []).map((o: any) => ({ value: o.id, label: o.network_name }));
    setNetworks(mappedNetworks);
    const nMode = modeToCard(normalized.network_mode);
    setNetworkMode(nMode);
    setIsNetworkActive(nMode !== null || mappedNetworks.length > 0 || normalized.network_all);
    setSelectAllNetworks(normalized.network_all ?? false);

    // Insurance
    const mappedInsurance = (Array.isArray(normalized.insuranceplan)
      ? normalized.insuranceplan.map((o: any) => typeof o === "string" ? { id: 0, Plan_name: o } : o)
      : []).map((o: any) => ({ value: o.id, label: o.Plan_name }));
    setInsuranceplan(mappedInsurance);
    const iMode = modeToCard(normalized.insuranceplan_mode);
    setInsuranceMode(iMode);
    setIsInsuranceActive(iMode !== null || mappedInsurance.length > 0 || normalized.insuranceplan_all);
    setSelectAllInsurance(normalized.insuranceplan_all ?? false);

    // Payer
    setPayer(normalized.payer || []);
    const payMode = modeToCard(normalized.payer_mode);
    setPayerMode(payMode);
    setIsPayerActive(payMode !== null || (normalized.payer?.length > 0) || normalized.payer_all);
    setSelectAllPayer(normalized.payer_all ?? false);

    // Policy
    const mappedPolicy = Array.isArray(normalized.policy)
      ? normalized.policy.map((o: any) => ({ id: o.id || 0, Policy_id: o.Policy_id || "", Policy_name: o.Policy_name || "", plans: o.plans || [] }))
      : [];
    setPolicy(mappedPolicy);
    const polMode = modeToCard(normalized.policy_mode);
    setPolicyMode(polMode);
    setIsPolicyActive(polMode !== null || mappedPolicy.length > 0 || normalized.policy_all);
    setSelectAllPolicy(normalized.policy_all ?? false);

    // Patients — backend returns "patient"
    const patientSource = normalized.patient ?? normalized.patients ?? [];
    const mappedPatients = (Array.isArray(patientSource)
      ? patientSource.map((o: any) => typeof o === "string" ? { id: 0, name: o } : o)
      : []).map((o: any) => ({ value: o.id, label: o.name }));
    setPatients(mappedPatients);
    const patMode = modeToCard(normalized.patient_mode);
    setPatientMode(patMode);
    setIsPatientActive(patMode !== null || mappedPatients.length > 0 || normalized.patient_all);
    setSelectAllPatients(normalized.patient_all ?? false);

    // VIP
    const mappedVip = (Array.isArray(normalized.vip)
      ? normalized.vip.map((o: any) => typeof o === "string" ? { id: 0, name: o } : o)
      : []).map((o: any) => ({ value: o.id, label: o.name }));
    setVip(mappedVip);
    const vMode = modeToCard(normalized.vip_mode);
    setVipMode(vMode);
    setIsVipActive(vMode !== null || mappedVip.length > 0 || normalized.vip_all);
    setSelectAllVip(normalized.vip_all ?? false);

    // User Level
    setUserLevel(normalized.user_level || []);
    const uMode = modeToCard(normalized.user_mode);
    setUserMode(uMode);
    setIsUserActive(uMode !== null || (normalized.user_level?.length > 0) || normalized.user_all);
    setSelectAllUser(normalized.user_all ?? false);

    // Specialty
    const specialtyData = Array.isArray(normalized.specialty)
      ? normalized.specialty.map((o: any) => typeof o === "string" ? { id: 0, name: o } : o)
      : [];
    const mappedSpecialty = specialtyData.map((o: any) => ({ value: o.id, label: o.name }));
    setSpecialty(mappedSpecialty);
    const spMode = modeToCard(normalized.specialty_mode);
    setSpecialtyMode(spMode);
    setIsSpecialtyActive(spMode !== null || mappedSpecialty.length > 0 || normalized.specialty_all);
    setSelectAllSpecialty(normalized.specialty_all ?? false);

    setEditIndex(normalized.id || (normalized as any)._id);

    const hasEntities = !!(
      mappedProviders.length > 0 || normalized.provider_all ||
      mappedDoctors.length > 0   || normalized.doctor_all ||
      mappedNetworks.length > 0  || normalized.network_all ||
      mappedInsurance.length > 0 || normalized.insuranceplan_all ||
      mappedPolicy.length > 0    || normalized.policy_all ||
      mappedPatients.length > 0  || normalized.patient_all ||
      mappedVip.length > 0       || normalized.vip_all ||
      normalized.payer?.length > 0 || normalized.payer_all ||
      normalized.user_level?.length > 0 || normalized.user_all ||
      mappedSpecialty.length > 0 || normalized.specialty_all ||
      (normalized.provider_mode      && normalized.provider_mode      !== "DISABLED") ||
      (normalized.doctor_mode        && normalized.doctor_mode        !== "DISABLED") ||
      (normalized.network_mode       && normalized.network_mode       !== "DISABLED") ||
      (normalized.insuranceplan_mode && normalized.insuranceplan_mode !== "DISABLED") ||
      (normalized.payer_mode         && normalized.payer_mode         !== "DISABLED") ||
      (normalized.policy_mode        && normalized.policy_mode        !== "DISABLED") ||
      (normalized.patient_mode       && normalized.patient_mode       !== "DISABLED") ||
      (normalized.vip_mode           && normalized.vip_mode           !== "DISABLED") ||
      (normalized.user_mode          && normalized.user_mode          !== "DISABLED") ||
      (normalized.specialty_mode     && normalized.specialty_mode     !== "DISABLED")
    );
    setShowEntities(hasEntities);
  };

  // ── build toggle payload ──────────────────────────────────────────────────
  const buildTogglePayload = (item: Result & { activity_types: CodeWithType[] }) => {
    const flat: CodeWithType[] = item.activity_types || [];
    const networkSource = item.network ?? item.networks ?? [];
    const patientSource = item.patient ?? item.patients ?? [];
    return {
      gender: item.gender,
      relation_name: item.relation_name,
      activity_type: uniqueTypes(flat),
      activity_code: flat.map((c) => c.code),
      activity_types: flat.map((c) => ({ code: c.code, type: c.type })),
      custom_activity_groups: (item.custom_activity_groups || []).map((g: any) => g.id ?? g),
      medical_activity_groups: (item.medical_activity_groups || []).map((g: any) => g.id ?? g),
      lower_age: item.lower_age,
      higher_age: item.higher_age,
      age_exclude: item.age_exclude,
      age_allowed: !item.age_exclude,
      is_active_to_age: item.is_active_to_age,
      is_active_to_gender: item.is_active_to_gender,
      provider: (item.provider || []).map((o: any) => ({ Provider_id: o.Provider_id || "", Provider_name: o.Provider_name || "" })),
      provider_mode: item.provider_mode ?? "DISABLED",
      provider_all: item.provider_all ?? false,
      network: (Array.isArray(networkSource) ? networkSource : []).map((n: any) => n.id ?? n),
      network_mode: item.network_mode ?? "DISABLED",
      network_all: item.network_all ?? false,
      insuranceplan: (item.insuranceplan || []).map((i: any) => i.id ?? i),
      insuranceplan_mode: item.insuranceplan_mode ?? "DISABLED",
      insuranceplan_all: item.insuranceplan_all ?? false,
      payer: item.payer || [],
      payer_mode: item.payer_mode ?? "DISABLED",
      payer_all: item.payer_all ?? false,
      patient: (Array.isArray(patientSource) ? patientSource : []).map((p: any) => p.name ?? p),
      patient_mode: item.patient_mode ?? "DISABLED",
      patient_all: item.patient_all ?? false,
      policy: buildPolicyValues(item.policy || []),
      policy_mode: item.policy_mode ?? "DISABLED",
      policy_all: item.policy_all ?? false,
      user_level: item.user_level || [],
      user_mode: item.user_mode ?? "DISABLED",
      user_all: item.user_all ?? false,
      vip: (item.vip || []).map((v: any) => v.id ?? v),
      vip_mode: item.vip_mode ?? "DISABLED",
      vip_all: item.vip_all ?? false,
      doctor: (item.doctor || []).map((d: any) => d.Doctor_name ?? d),
      doctor_mode: item.doctor_mode ?? "DISABLED",
      doctor_all: item.doctor_all ?? false,
      specialty: (item.specialty || []).map((s: any) => s.id ?? s),
      specialty_mode: item.specialty_mode ?? "DISABLED",
      specialty_all: item.specialty_all ?? false,
      id: item.id,
      token,
    };
  };

  // ── toggleActiveStatus ────────────────────────────────────────────────────
  const toggleActiveStatus = async (item: Result & { activity_types: CodeWithType[] }) => {
    try {
      const payload = { ...buildTogglePayload(item), is_active: !item.is_active };
      const response = await ActivityAgeGenderAxiosInstance.put("updateActivityagetogender", payload);
      if (response.data.success) {
        setItems((prev) => ({ result: prev.result.map((i) => i.id === item.id ? { ...i, is_active: !item.is_active } : i) }));
        toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const toggleAgeGenderStatus = async (item: Result & { activity_types: CodeWithType[] }, field: "age" | "gender") => {
    try {
      const payload = {
        ...buildTogglePayload(item),
        is_active: item.is_active,
        is_active_to_age: field === "age" ? !item.is_active_to_age : item.is_active_to_age,
        is_active_to_gender: field === "gender" ? !item.is_active_to_gender : item.is_active_to_gender,
      };
      const response = await ActivityAgeGenderAxiosInstance.put("updateActivityagetogender", payload);
      if (response.data.success) {
        setItems((prev) => ({
          result: prev.result.map((i) =>
            i.id === item.id
              ? {
                  ...i,
                  is_active_to_age: field === "age" ? !item.is_active_to_age : i.is_active_to_age,
                  is_active_to_gender: field === "gender" ? !item.is_active_to_gender : i.is_active_to_gender,
                }
              : i
          ),
        }));
        toast({ title: "Success", description: `${field === "age" ? "Age" : "Gender"} validation status updated.` });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};
const userRole = getUserRole();
const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

const [forceDialogOpen, setForceDialogOpen] = useState(false);
const [forceId, setForceId] = useState<string | number | null>(null);
const [forceError, setForceError] = useState<string>("");
const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | null>(null);

const removeItem = async (id: string | number): Promise<void> => {
  try {
    const response = await ActivityAgeGenderAxiosInstance.delete(
      `softDeleteActivityagetogender/${id}`, { data: { token } }
    );
    if (response.data.success) {
      await fetchItems();
      toast({ title: "Success", description: "Rule deleted successfully." });
    } else {
      setForceId(id); setForceAction("softDelete");
      setForceError(response.data.error || "This record is used elsewhere.");
      setForceDialogOpen(true);
    }
  } catch (error: any) {
    const errMsg = error.response?.data?.error || "Failed to delete";
    if (error.response?.status === 409 || errMsg.toLowerCase().includes("used") || errMsg.toLowerCase().includes("referenced")) {
      setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
    } else {
      toast({ title: "Error", description: errMsg, variant: "destructive" });
    }
  }
};

const restoreItem = async (id: string | number): Promise<void> => {
  try {
    const response = await ActivityAgeGenderAxiosInstance.patch(
      `restoreActivityagetogender/${id}`, { token }
    );
    if (response.data.success) {
      await fetchItems();
      toast({ title: "Success", description: "Rule restored successfully." });
    } else {
      toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
    }
  } catch (error: any) {
    toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
  }
};

const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
  try {
    const url = force ? `deleteActivityagetogender/${id}?force=true` : `deleteActivityagetogender/${id}`;
    const response = await ActivityAgeGenderAxiosInstance.delete(url, { data: { token } });
    if (response.data.success) {
      await fetchItems();
      setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
      toast({ title: "Success", description: "Rule permanently deleted." });
    } else {
      setForceAction("hardDelete");
      setForceError(response.data.error || "This record is used elsewhere.");
    }
  } catch (error: any) {
    setForceAction("hardDelete");
    setForceError(error.response?.data?.error || "Failed to delete");
  }
};

const forceSoftDelete = async (id: string | number): Promise<void> => {
  try {
    const response = await ActivityAgeGenderAxiosInstance.delete(
      `softDeleteActivityagetogender/${id}?force=true`, { data: { token } }
    );
    if (response.data.success) {
      await fetchItems();
      setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
      toast({ title: "Success", description: "Rule force deleted successfully." });
    } else {
      setForceError(response.data.error || "Failed to force delete");
    }
  } catch (error: any) {
    setForceError(error.response?.data?.error || "Failed to force delete");
  }
};


  const handleDownload = (item: Result & { activity_types: CodeWithType[] }) => {
    const flat: CodeWithType[] = item.activity_types || [];
    const rows = ["relation_name;activity_code;activity_type;custom_activity_groups;medical_activity_groups;lower_age;higher_age;age_exclude;is_active_to_age;is_active_to_gender;provider;provider_mode;provider_all;network;network_mode;network_all;insuranceplan;insuranceplan_mode;insuranceplan_all;payer;payer_mode;payer_all;patient;patient_mode;patient_all;policy;policy_mode;policy_all;user_level;user_mode;user_all;doctor;doctor_mode;doctor_all;vip;vip_mode;vip_all;specialty;specialty_mode;specialty_all;is_active"];
    const providers  = item.provider || [];
    const nets       = item.network ?? item.networks ?? [];
    const insplans   = item.insuranceplan || [];
    const pats       = item.patient ?? item.patients ?? [];
    const pols       = item.policy || [];
    const uls        = item.user_level || [];
    const docs       = item.doctor || [];
    const pays       = item.payer || [];
    const vips       = item.vip || [];
    const custom     = item.custom_activity_groups || [];
    const medical    = item.medical_activity_groups || [];
    const specs      = item.specialty || [];
    const maxLen = Math.max(flat.length, custom.length, medical.length, providers.length, nets.length, insplans.length, pats.length, pols.length, uls.length, docs.length, pays.length, vips.length, specs.length, 1);
    for (let i = 0; i < maxLen; i++) {
      rows.push([
        item.relation_name || "",
        i < flat.length ? flat[i].code : "",
        i < flat.length ? flat[i].type : "",
        i < custom.length  ? (custom[i]  as any).name || "" : "",
        i < medical.length ? (medical[i] as any).name || "" : "",
        item.lower_age || "", item.higher_age || "", item.age_exclude || "",
        item.is_active_to_age ? "TRUE" : "FALSE",
        item.is_active_to_gender ? "TRUE" : "FALSE",
        i < providers.length ? (providers[i] as any).Provider_name || "" : "",
        item.provider_mode ?? "DISABLED", item.provider_all ? "true" : "false",
        i < nets.length ? (nets[i] as any).network_name || "" : "",
        item.network_mode ?? "DISABLED", item.network_all ? "true" : "false",
        i < insplans.length ? (insplans[i] as any).Plan_name || "" : "",
        item.insuranceplan_mode ?? "DISABLED", item.insuranceplan_all ? "true" : "false",
        i < pays.length ? pays[i] : "",
        item.payer_mode ?? "DISABLED", item.payer_all ? "true" : "false",
        i < pats.length ? (pats[i] as any).name || "" : "",
        item.patient_mode ?? "DISABLED", item.patient_all ? "true" : "false",
        i < pols.length ? (pols[i] as any).Policy_name || "" : "",
        item.policy_mode ?? "DISABLED", item.policy_all ? "true" : "false",
        i < uls.length ? uls[i] : "",
        item.user_mode ?? "DISABLED", item.user_all ? "true" : "false",
        i < docs.length ? (docs[i] as any).Doctor_name || "" : "",
        item.doctor_mode ?? "DISABLED", item.doctor_all ? "true" : "false",
        i < vips.length ? (vips[i] as any).name || "" : "",
        item.vip_mode ?? "DISABLED", item.vip_all ? "true" : "false",
        i < specs.length ? (specs[i] as any).name || "" : "",
        item.specialty_mode ?? "DISABLED", item.specialty_all ? "true" : "false",
        item.is_active ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ActivityAgeAndGender${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Dropdown style helpers ──
  const ddBtn = (active: boolean, isGreen = false): React.CSSProperties => ({
    display: "flex", alignItems: "center", gap: 7, padding: "0 16px", height: 38,
    borderRadius: 9999, border: active ? `1.5px solid ${isGreen ? "#10b981" : "#3b82f6"}` : "1.5px solid #e2e8f0",
    background: active ? isGreen ? "#f0fdf4" : "#eff6ff" : "#ffffff",
    cursor: "pointer", fontSize: 13, fontWeight: 500,
    color: active ? isGreen ? "#065f46" : "#1d4ed8" : "#64748b",
    whiteSpace: "nowrap" as const, transition: "border-color .15s, background .15s", zIndex: 10,
  });
  const ddMenu: React.CSSProperties = {
    position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#fff",
    border: "1px solid #e2e8f0", borderRadius: 12, padding: 4, minWidth: 170, zIndex: 200,
    boxShadow: "0 6px 20px rgba(0,0,0,.10)",
  };
  const ddSectionLabel: React.CSSProperties = { fontSize: 11, color: "#94a3b8", padding: "5px 12px 3px", fontWeight: 600, textTransform: "capitalize", letterSpacing: ".5px" };
  const ddSeparator: React.CSSProperties = { height: 1, background: "#f1f5f9", margin: "3px 4px" };
  const ddItem = (selected: boolean, isGreen = false): React.CSSProperties => ({
    display: "flex", alignItems: "center", gap: 9, padding: "8px 12px", borderRadius: 8,
    cursor: "pointer", fontSize: 13,
    background: selected ? isGreen ? "#f0fdf4" : "#eff6ff" : "transparent",
    color: selected ? isGreen ? "#065f46" : "#1d4ed8" : "#1e293b",
    fontWeight: selected ? 500 : 400, transition: "background .1s",
  });
  const chevron = (open: boolean): React.CSSProperties => ({ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 });
  const columns: ColumnDef<Result & { activity_types: CodeWithType[] }>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID {column.getIsSorted() === "asc" ? "↑" : column.getIsSorted() === "desc" ? "↓" : ""}
        </Button>
      ),
    cell: ({ row }) => (
  <div className="w-full flex justify-center font-medium">{row.index + 1}</div>
),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name {column.getIsSorted() === "asc" ? "↑" : column.getIsSorted() === "desc" ? "↓" : ""}
        </Button>
      ),
cell: ({ row }) => {
  const isDeleted = (row.original as any).is_deleted;
  return (
    <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
      {row.getValue("relation_name")}
    </div>
  );
},
    },
    {
      accessorKey: "activity_code",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity Code {column.getIsSorted() === "asc" ? "↑" : column.getIsSorted() === "desc" ? "↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const actTypes: CodeWithType[] = item.activity_types || [];
        const custom = Array.isArray(item.custom_activity_groups) ? item.custom_activity_groups.map((g: any) => g.name || g) : [];
        const medical = Array.isArray(item.medical_activity_groups) ? item.medical_activity_groups.map((g: any) => g.name || g) : [];
        const groups = [...medical.map((g: string) => `[Medical] ${g}`), ...custom.map((g: string) => `[Custom] ${g}`)];
        return (
          <ActivityTypeCell items={actTypes} groups={groups}
            onShowAll={(its, grps) => { setAllCodesItems(its); setAllCodesGroups(grps); setAllCodesTitle(`${item.relation_name} – Activity Codes`); setAllCodesOpen(true); }}
          />
        );
      },
    },
    {
      accessorKey: "gender",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Gender {column.getIsSorted() === "asc" ? "↑" : column.getIsSorted() === "desc" ? "↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const g = (row.getValue("gender") as string) || "";
        if (!g) return <div className="w-full text-center text-gray-400">—</div>;
        const n = g.toLowerCase();
        const display = n === "male" ? "Male" : n === "female" || n === "feamal" ? "Female" : g.charAt(0).toUpperCase() + g.slice(1).toLowerCase();
        const isMale = n === "male";
        return (
          <div className="w-full flex justify-center">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isMale ? "bg-blue-100 text-blue-800" : "bg-pink-100 text-pink-800"}`}>{display}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active_to_gender",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
         Gender Status {column.getIsSorted() === "asc" ? "↑" : column.getIsSorted() === "desc" ? "↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center">
          <button
  onClick={() => !(item as any).is_deleted && toggleAgeGenderStatus(item, "gender")}
  disabled={!!(item as any).is_deleted}
  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${(item as any).is_deleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active_to_gender ? "bg-green-500" : "bg-gray-300"}`}>
  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active_to_gender && !(item as any).is_deleted ? "translate-x-6" : "translate-x-1"}`} />
</button>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active_to_age",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Age Status {column.getIsSorted() === "asc" ? "↑" : column.getIsSorted() === "desc" ? "↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center">
          <button
  onClick={() => !(item as any).is_deleted && toggleAgeGenderStatus(item, "age")}
  disabled={!!(item as any).is_deleted}
  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${(item as any).is_deleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active_to_age ? "bg-green-500" : "bg-gray-300"}`}>
  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active_to_age && !(item as any).is_deleted ? "translate-x-6" : "translate-x-1"}`} />
</button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <Button variant="ghost" className="w-full justify-center">View</Button>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={() => handleOpenDialog(row.original)} className="text-blue-600 hover:text-blue-800 transition-colors" title="View Details">
            <i className="fa-solid fa-eye text-lg" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center mb-6">
        <h5 className="flex items-center gap-3 font-bold text-2xl text-blue-900 mt-8">
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-user-plus" />
          </span>
          Activity: Age and Gender
        </h5>
      </div>

      <DataTable2
        columns={columns}
        data={items.result}
        searchColumns={[]}
        addButtonLabel="Add New Relation"
        validateButtonLabel="Validate"
        showAddButton={false}
        onEdit={handleEdit}
onDelete={(id) => {
  const item = items.result.find(i => i.id === id);
  if ((item as any)?.is_deleted) {
    setForceId(id); setForceAction("hardDelete"); setForceError(""); setForceDialogOpen(true);
  } else {
    removeItem(id);
  }
}}
onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
isRowDeleted={(row: any) => row.is_deleted === true}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDownload={handleDownload}
        toolbarExtras={
          <div style={{ width: "100%", position: "relative" }}>
<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                Add New Relation
              </button>
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Relation Name, Activity Code, Medical Group, or Custom Group."
              />
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              {/* Gender dropdown */}
              <div style={{ position: "relative", flexShrink: 0 }} ref={genderRef}>
                <button type="button" onClick={() => { setGenderOpen(o => !o); setGValidOpen(false); setAValidOpen(false); }} style={ddBtn(!!filterGender)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={filterGender ? "#3b82f6" : "#94a3b8"} strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
                  {filterGender === "male" ? "Male" : filterGender === "female" ? "Female" : "Gender"}
                  <svg style={chevron(genderOpen)} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                {genderOpen && (
                  <div style={ddMenu}>
                    <div style={ddSectionLabel}>Gender</div>
                    {[{ val: "", label: "All", color: "#94a3b8" }, { val: "male", label: "Male", color: "#3b82f6" }, { val: "female", label: "Female", color: "#ec4899" }].map((opt, i) => (
                      <React.Fragment key={opt.val}>
                        {i === 1 && <div style={ddSeparator} />}
                        <div onClick={() => { setFilterGender(opt.val); setGenderOpen(false); }} style={ddItem(filterGender === opt.val)}>
                          <span style={{ width: 9, height: 9, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />{opt.label}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              {/* Gender Validation dropdown */}
              <div style={{ position: "relative", flexShrink: 0 }} ref={gValidRef}>
                <button type="button" onClick={() => { setGValidOpen(o => !o); setGenderOpen(false); setAValidOpen(false); }} style={ddBtn(!!filterGenderValidation, true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={filterGenderValidation ? "#10b981" : "#94a3b8"} strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 3" /></svg>
                  {filterGenderValidation === "true" ? "Active" : filterGenderValidation === "false" ? "Inactive" : "G. Status"}   
                                 <svg style={chevron(gValidOpen)} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                {gValidOpen && (
                  <div style={ddMenu}>
                    <div style={ddSectionLabel}>Gender Status</div>
                    {[{ val: "", label: "All", color: "#94a3b8" }, { val: "true", label: "Active", color: "#10b981" }, { val: "false", label: "Inactive", color: "#ef4444" }].map((opt, i) => (
                      <React.Fragment key={opt.val}>
                        {i === 1 && <div style={ddSeparator} />}
                        <div onClick={() => { setFilterGenderValidation(opt.val); setGValidOpen(false); }} style={ddItem(filterGenderValidation === opt.val, true)}>
                          <span style={{ width: 9, height: 9, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />{opt.label}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              {/* Age Validation dropdown */}
              <div style={{ position: "relative", flexShrink: 0 }} ref={aValidRef}>
                <button type="button" onClick={() => { setAValidOpen(o => !o); setGenderOpen(false); setGValidOpen(false); }} style={ddBtn(!!filterAgeValidation, true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={filterAgeValidation ? "#10b981" : "#94a3b8"} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                  {filterAgeValidation === "true" ? "Active" : filterAgeValidation === "false" ? "Inactive" : "A. Status"}
                  <svg style={chevron(aValidOpen)} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                {aValidOpen && (
                  <div style={ddMenu}>
                    <div style={ddSectionLabel}>Age Status</div>
                    {[{ val: "", label: "All", color: "#94a3b8" }, { val: "true", label: "Active", color: "#10b981" }, { val: "false", label: "Inactive", color: "#ef4444" }].map((opt, i) => (
                      <React.Fragment key={opt.val}>
                        {i === 1 && <div style={ddSeparator} />}
                        <div onClick={() => { setFilterAgeValidation(opt.val); setAValidOpen(false); }} style={ddItem(filterAgeValidation === opt.val, true)}>
                          <span style={{ width: 9, height: 9, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />{opt.label}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />

      {/* ══ VIEW DIALOG ══════════════════════════════════════════════════════ */}
      <Dialog open={viewDialogOpen} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="max-w-[900px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-user-check" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0 }}>{selectedItem.relation_name}</h2>
                    </div>
                    <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Rule ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
{(() => {
  const genderInactive = !selectedItem.is_active_to_gender;
  const ageInactive = !selectedItem.is_active_to_age;

  const cards = [
    {
      icon: "fa-venus-mars",
      label: "Gender",
      value: selectedItem.gender === "male" ? "Male" : selectedItem.gender === "female" || selectedItem.gender === "feamal" ? "Female" : "Both",
      accent: "#f472b6",
      warning: genderInactive,
    },
    {
      icon: "fa-clock",
      label: "Age Range",
      value: `${selectedItem.lower_age ?? "—"} – ${selectedItem.higher_age ?? "—"}`,
      accent: "#60a5fa",
      warning: ageInactive,
    },
    {
      icon: "fa-check-circle",
      label: "Gender Status",
      value: selectedItem.is_active_to_gender ? "Active" : "Inactive",
      accent: selectedItem.is_active_to_gender ? "#34d399" : "#f87171",
      warning: false,
    },
    {
      icon: "fa-check-circle",
      label: "Age Status",
      value: selectedItem.is_active_to_age ? "Active" : "Inactive",
      accent: selectedItem.is_active_to_age ? "#34d399" : "#f87171",
      warning: false,
    },
  ];

  return cards.map((card, i) => (
    <div
      key={i}
      style={{
        background: card.warning ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.07)",
border: card.warning ? "1px solid rgba(239,68,68,0.7)" : "1px solid rgba(255,255,255,0.08)",
        borderRadius: 12,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <i
          className={`fa-solid ${card.icon}`}
          style={{ fontSize: 11, color: card.warning ? "#f87171" : card.accent }}
        />
        <span style={{ fontSize: 10, color: card.warning ? "rgba(252,165,165,0.85)" : "rgba(147,197,253,0.7)", fontWeight: 500, letterSpacing: "0.06em" }}>
          {card.label}
        </span>
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color: card.warning ? "#fca5a5" : card.accent }}>
        {card.value}
      </span>
    </div>
  ));
})()}

                </div>
              </div>

              <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "20px 28px" }}>
                {/* Activity */}
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Activity Configuration</span>
            {(() => {
  const flat: CodeWithType[] = selectedItem.activity_types || [];
  const grouped: Record<string, string[]> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
  flat.forEach(({ code, type }) => { if (grouped[type]) grouped[type].push(String(code)); });

  const medicalGroups = Array.isArray(selectedItem.medical_activity_groups)
    ? selectedItem.medical_activity_groups.map((g: any) => g.name || g)
    : [];
  const customGroups = Array.isArray(selectedItem.custom_activity_groups)
    ? selectedItem.custom_activity_groups.map((g: any) => g.name || g)
    : [];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12, marginBottom: 20 }}>
      {Object.entries(grouped).map(([type, codes]) => codes.length === 0 ? null : (
        <div key={type} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>{type} Codes ({codes.length})</div>
          <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
            {codes.map((code, i) => <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#eff6ff", color: "#1d4ed8", border: "0.5px solid #bfdbfe" }}>{code}</span>)}
          </div>
        </div>
      ))}

      {medicalGroups.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>Medical Activity Groups ({medicalGroups.length})</div>
          <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
            {medicalGroups.map((g: string, i: number) => (
              <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" }}>{g}</span>
            ))}
          </div>
        </div>
      )}

      {customGroups.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>Custom Activity Groups ({customGroups.length})</div>
          <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
            {customGroups.map((g: string, i: number) => (
              <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" }}>{g}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
})()}

                <div style={{ height: 1, background: "#e2e8f0", marginBottom: 20 }} />

                {/* Target Scope */}
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Target Scope</span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
                  {[
                  { label: "Providers", icon: "fa-hospital", color: "#3b82f6", items: (selectedItem.provider || []).map((p: any) => p.Provider_name || String(p)), mode: selectedItem.provider_mode, all: selectedItem.provider_all },
                    { label: "Doctors", icon: "fa-stethoscope", color: "#06b6d4", items: (selectedItem.doctor || []).map((d: any) => d.Doctor_id ? `(${d.Doctor_id}) ${d.Doctor_name}` : d.Doctor_name || String(d)), mode: selectedItem.doctor_mode, all: selectedItem.doctor_all },
                    { label: "Networks", icon: "fa-network-wired", color: "#8b5cf6", items: (selectedItem.networks ?? (selectedItem as any).network ?? []).map((n: any) => n.network_name || String(n)), mode: selectedItem.network_mode, all: selectedItem.network_all },
                    { label: "Insurance Plans", icon: "fa-shield-halved", color: "#f59e0b", items: (selectedItem.insuranceplan || []).map((i: any) => i.Plan_name || String(i)), mode: selectedItem.insuranceplan_mode, all: selectedItem.insuranceplan_all },
                    { label: "Policies", icon: "fa-file-lines", color: "#10b981", items: (selectedItem.policy || []).map((p: any) => `${p.Policy_id || ""} - ${p.Policy_name || ""}`), mode: selectedItem.policy_mode, all: selectedItem.policy_all },
                    { label: "Patients", icon: "fa-users", color: "#ec4899", items: (selectedItem.patients ?? (selectedItem as any).patient ?? []).map((p: any) => p.name || String(p)), mode: selectedItem.patient_mode, all: selectedItem.patient_all },
                    { label: "VIP", icon: "fa-star", color: "#eab308", items: (selectedItem.vip || []).map((v: any) => v.name || String(v)), mode: selectedItem.vip_mode, all: selectedItem.vip_all },
                    { label: "Payer", icon: "fa-building-columns", color: "#6366f1", items: (selectedItem.payer || []).map((p: any) => String(p)), mode: selectedItem.payer_mode, all: selectedItem.payer_all },
                    { label: "User Levels", icon: "fa-user-gear", color: "#64748b", items: (selectedItem.user_level || []).map((u: any) => String(u)), mode: selectedItem.user_mode, all: selectedItem.user_all },
                    { label: "Specialty", icon: "fa-briefcase-medical", color: "#0d9488", items: (selectedItem.specialty || []).map((s: any) => s.name || String(s)), mode: selectedItem.specialty_mode, all: selectedItem.specialty_all },
                  ].map((e) => {
                const isExclude = e.mode === "EXCLUDED";
                    const isDisabled = !e.mode || e.mode === "DISABLED";
                    const isAll = !!e.all;
                    return (
                      <div key={e.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <i className={`fa-solid ${e.icon}`} style={{ fontSize: 11, color: e.color }} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{e.label}</span>
                          </div>
                          {!isDisabled && (
                            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600, background: isExclude ? "#fef2f2" : "#f0fdf4", color: isExclude ? "#dc2626" : "#15803d", border: `1px solid ${isExclude ? "#fecaca" : "#bbf7d0"}` }}>
                              {isExclude ? "Exclusion" : "Inclusion"}
                            </span>
                          )}
                        </div>
                       {isAll
                          ? <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}><i className="fa-solid fa-check-double" style={{ fontSize: 9 }} /> All {e.label}</span>
                          : e.items.length > 0
                            ? <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>{e.items.map((item: string, i: number) => <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999, background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", fontWeight: 500 }}>{item}</span>)}</div>
                            : <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>No items</span>}
                      </div>
                    );
                  })}
                </div>

                {/* Audit */}
                <div style={{ height: 1, background: "#e2e8f0", margin: "20px 0" }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                        {String(selectedItem.created_by ?? "?")[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(selectedItem.created_by)}</span>
                    </div>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{selectedItem.createdat ? new Date(selectedItem.createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                    </div>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{selectedItem.updatedat ? new Date(selectedItem.updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                    </div>
                  </div>
                </div>
              </div>
            
{(() => {
  const updatedBy: { id?: number; at?: string }[] = (selectedItem as any).updated_by;
  if (!Array.isArray(updatedBy) || updatedBy.length === 0) return null;
  return (
    <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
        <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
        <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
          {updatedBy.length} update{updatedBy.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {updatedBy.slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
              {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
            </div>
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
              {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
            </span>
          </div>
        ))}
        {updatedBy.length > 4 && (
          <div style={{ textAlign: "center", padding: "6px 0" }}>
            <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
              {showAllUpdates ? "Show less ↑" : `+${updatedBy.length - 4} more updates ↓`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
})()}
  <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                <Button onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10 }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* ══ ADD / EDIT DIALOG ════════════════════════════════════════════════ */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl h-[92vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Relation  rule" : "Edit activity rule"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {editIndex === null ? "Fill in the details to create a new age and gender rule." : "Update the details for this rule."}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic information" subtitle="Name and overall rule status">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="relation_name">Relation name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input id="relation_name" value={relationName} onChange={(e) => setRelationName(e.target.value)} placeholder="Enter a name for this rule" className="mt-1" />
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="Gender and age criteria" subtitle="Define who this rule applies to">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="mb-2 block">Gender</Label>
                  <div className="flex gap-2">
                    {[
                      { val: "female", label: "Female", active: "border-pink-400 bg-pink-50 text-pink-700" },
                      { val: "male",   label: "Male",   active: "border-blue-500 bg-blue-50 text-blue-700" },
                    ].map(({ val, label, active }) => (
                      <button key={val} type="button" onClick={() => setGender(val)}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all ${gender === val ? active : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="mb-2 block">Age range (years)</Label>
                  <div className="flex items-center gap-3">
                    <Input type="number" value={lowerAge ?? ""} onChange={(e) => { const v = e.target.value ? parseInt(e.target.value) : null; setLowerAge(v); validateAge(v, higherAge); }} placeholder="From" min={0} max={120} className="flex-1" />
                    <span className="text-gray-400 text-lg font-light flex-shrink-0">—</span>
                    <Input type="number" value={higherAge ?? ""} onChange={(e) => { const v = e.target.value ? parseInt(e.target.value) : null; setHigherAge(v); validateAge(lowerAge, v); }} placeholder="To" min={0} max={120} className="flex-1" />
                  </div>
                  {ageError && <p className="mt-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{ageError}</p>}
                </div>
                <StatusToggle label="Gender Status" dotColor="#D4537E" value={isActiveToGender} onChange={setIsActiveToGender} />
                <StatusToggle label="Age Status"    dotColor="#378ADD" value={isActiveToAge}    onChange={setIsActiveToAge}    />
              </div>
            </SectionCard>

            {/* Section 3 */}
            <SectionCard num={3} title="Activity configuration" subtitle="Which activity codes or groups this rule covers">
              <div className="space-y-3">
                <div>
                  <Label className="mb-1.5 block">Activity source</Label>
                  <Select value={selectedMainOption || undefined} onValueChange={(v) => setSelectedMainOption(v)}>
                    <SelectTrigger><SelectValue placeholder="Select an option…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="list_of_Activity_code">List of activity code</SelectItem>
                      <SelectItem value="medical_Activity_groups">Medical activity groups</SelectItem>
                      <SelectItem value="custom_Activity_groups">Custom activity groups</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedMainOption === "list_of_Activity_code" && (
                  <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-4 space-y-3">
                    <h3 className="text-sm font-bold text-blue-900 flex items-center gap-2"><i className="fa-solid fa-code" /> Activity Codes by Type</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {(["CPT", "HCPCS", "Dental", "Drugs"] as const).map((key) => {
                        const colMap: Record<string, string> = { CPT: "blue", HCPCS: "green", Dental: "purple", Drugs: "orange" };
                        const c = colMap[key];
                        return (
                          <div key={key} className={`bg-white p-3 rounded-lg border border-${c}-200`}>
                            <Label className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 bg-${c}-100 text-${c}-900 rounded text-xs font-bold border border-${c}-400`}>{key}</span>
                              <span className="text-xs text-gray-500">({activitiesByType[key].length} codes)</span>
                            </Label>
                            <ListofActivityCode onChange={(selected: Option[]) => setActivitiesByType(prev => ({ ...prev, [key]: selected }))} type={key} defaultValue={activitiesByType[key]} clear={false} isValid={true} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {selectedMainOption === "medical_Activity_groups" && (
                  <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                    <ListOfActivityMedical onChange={setMedicalActivityGroups} defaultValue={medicalActivityGroups} clear={false} />
                  </div>
                )}
                {selectedMainOption === "custom_Activity_groups" && (
                  <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                    <ListOfActivityCustom onChange={setCustomActivityGroups} defaultValue={customActivityGroups} clear={false} />
                  </div>
                )}
              </div>
            </SectionCard>

            {/* ===== ENTITIES CONFIGURATION ===== */}
            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50 space-y-6 mb-4">
              <div className="flex items-center justify-between border-b border-blue-200 pb-4">
                <div className="flex items-center gap-2 text-blue-900">
                  <i className="fa-solid fa-layer-group text-lg"></i>
                  <h2 className="text-base font-bold tracking-tight">Entities Configuration</h2>
                </div>
                <button
                  type="button"
                  onClick={handleToggleEntities}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>

              {showEntities && (
                <div className="grid grid-cols-2 gap-4">

                  {/* Provider */}
                  <EntityCard label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                    onExclusion={() => { if (providerMode === "EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("EXCLUSION"); setIsProviderActive(true); } }}
                    onInclusion={() => { if (providerMode === "INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("INCLUSION"); setIsProviderActive(true); } }}
                    onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllProviders} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Providers</span>
                    </label>
                    {!selectAllProviders && <ListOfProviderName onChange={setProvider} defaultValue={provider} clear={clearProvider} />}
                  </EntityCard>

                  {/* Doctor */}
                  <EntityCard label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                    onExclusion={() => { if (doctorMode === "EXCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("EXCLUSION"); setIsDoctorActive(true); } }}
                    onInclusion={() => { if (doctorMode === "INCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("INCLUSION"); setIsDoctorActive(true); } }}
                    onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Doctors</span>
                    </label>
                    {!selectAllDoctor && <ListOfDoctorName onChange={setDoctor} defaultValue={doctor} clear={clearDoctor} />}
                  </EntityCard>

                  {/* Networks */}
                  <EntityCard label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                    onExclusion={() => { if (networkMode === "EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("EXCLUSION"); setIsNetworkActive(true); } }}
                    onInclusion={() => { if (networkMode === "INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("INCLUSION"); setIsNetworkActive(true); } }}
                    onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Networks</span>
                    </label>
                    {!selectAllNetworks && <ListOfNewNetworkName onChange={(v: Option | Option[] | null) => { if (!v) { setNetworks([]); return; } setNetworks(Array.isArray(v) ? v : [v]); }} defaultValue={networks} clear={clearNetworks} />}
                  </EntityCard>

                  {/* Insurance Plan */}
                  <EntityCard label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                    onExclusion={() => { if (insuranceMode === "EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); } }}
                    onInclusion={() => { if (insuranceMode === "INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); } }}
                    onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Insurance Plans</span>
                    </label>
                    {!selectAllInsurance && <ListOfInsuranceplanName onChange={setInsuranceplan} defaultValue={insuranceplan} clear={clearInsuranceplan} />}
                  </EntityCard>

                  {/* Policy */}
                  <EntityCard label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                    onExclusion={() => { if (policyMode === "EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("EXCLUSION"); setIsPolicyActive(true); } }}
                    onInclusion={() => { if (policyMode === "INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("INCLUSION"); setIsPolicyActive(true); } }}
                    onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Policies</span>
                    </label>
                    {!selectAllPolicy && <PolicyNames onChange={(v: PolicyItem[]) => setPolicy(v)} defaultValue={policy} clear={clearPolicy} />}
                  </EntityCard>

                  {/* Patients */}
                  <EntityCard label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                    onExclusion={() => { if (patientMode === "EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("EXCLUSION"); setIsPatientActive(true); } }}
                    onInclusion={() => { if (patientMode === "INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("INCLUSION"); setIsPatientActive(true); } }}
                    onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllPatients} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Patients</span>
                    </label>
                    {!selectAllPatients && <VipPatientSelector onChange={setPatients} defaultValue={patients} clear={clearPatient} />}
                  </EntityCard>

                  {/* VIP */}
                  <EntityCard label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                    onExclusion={() => { if (vipMode === "EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("EXCLUSION"); setIsVipActive(true); } }}
                    onInclusion={() => { if (vipMode === "INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("INCLUSION"); setIsVipActive(true); } }}
                    onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllVip} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" />
                      <span className="text-sm font-medium text-slate-600">Select All VIP</span>
                    </label>
                    {!selectAllVip && <Vip onChange={setVip} defaultValue={vip} clear={clearVip} />}
                  </EntityCard>

                  {/* Specialty */}
                  <EntityCard label="Specialty" icon="fa-briefcase-medical" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                    onExclusion={() => { if (specialtyMode === "EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); } }}
                    onInclusion={() => { if (specialtyMode === "INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); } }}
                    onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Specialties</span>
                    </label>
                    {!selectAllSpecialty && <ListOfSpecialty onChange={setSpecialty} defaultValue={specialty} clear={clearSpecialty} />}
                  </EntityCard>

                  {/* Payer */}
                  <EntityCard label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                    onExclusion={() => { if (payerMode === "EXCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("EXCLUSION"); setIsPayerActive(true); } }}
                    onInclusion={() => { if (payerMode === "INCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("INCLUSION"); setIsPayerActive(true); } }}
                    onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllPayer} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" />
                      <span className="text-sm font-medium text-slate-600">Select All Payers</span>
                    </label>
                    {!selectAllPayer && <Input value={Payer.join(",")} onChange={(e) => setPayer([e.target.value])} placeholder="Enter Payer" />}
                  </EntityCard>

                  {/* User Level */}
                  <EntityCard label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                    onExclusion={() => { if (userMode === "EXCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("EXCLUSION"); setIsUserActive(true); } }}
                    onInclusion={() => { if (userMode === "INCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("INCLUSION"); setIsUserActive(true); } }}
                    onClear={() => { setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false); }}>
                    <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                      <input type="checkbox" checked={selectAllUser} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllUser(c); if (c) setUserLevel([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-slate-600" />
                      <span className="text-sm font-medium text-slate-600">Select All User Levels</span>
                    </label>
                    {!selectAllUser && <Input value={userLevel.join(",")} onChange={(e) => setUserLevel([e.target.value])} placeholder="Enter user level…" />}
                  </EntityCard>

                </div>
              )}
            </div>
            {/* ===== END ENTITIES ===== */}
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button onClick={() => editIndex === null ? addItem() : updateItem(editIndex)} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Relation " : "Update Relation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ ALL CODES DIALOG ═════════════════════════════════════════════════ */}
      <Dialog open={allCodesOpen} onOpenChange={setAllCodesOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list" /> {allCodesTitle}
              <span className="ml-auto text-sm font-normal text-gray-500">({allCodesItems.length + allCodesGroups.length} items)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] pr-1">
            {allCodesItems.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {allCodesItems.map((itm, idx) => {
                  const col = getActivityTypeColor(itm.type);
                  return (
                    <div key={idx} className={`flex items-center justify-between gap-2 p-2.5 bg-white rounded-lg border hover:shadow-sm transition-all ${col.border}`}>
                      <span className={`font-bold text-sm ${col.text}`}>{itm.code}</span>
                      <span className={`text-[10px] font-bold uppercase ${col.text} opacity-70`}>{itm.type}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {allCodesGroups.length > 0 && (
              <div className="mt-3 space-y-3">
                {allCodesGroups.filter(g => g.startsWith("[Medical] ")).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: "#16a34a", display: "inline-block" }} />
                      <span className="text-xs font-bold text-green-700">Medical Groups ({allCodesGroups.filter(g => g.startsWith("[Medical] ")).length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {allCodesGroups.filter(g => g.startsWith("[Medical] ")).map((g, idx) => (
                        <span key={idx} style={{ padding: "4px 12px", background: "#f0fdf4", color: "#15803d", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "1px solid #86efac" }}>{g.replace("[Medical] ", "")}</span>
                      ))}
                    </div>
                  </div>
                )}
                {allCodesGroups.filter(g => g.startsWith("[Custom] ")).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: "#9333ea", display: "inline-block" }} />
                      <span className="text-xs font-bold text-purple-700">Custom Groups ({allCodesGroups.filter(g => g.startsWith("[Custom] ")).length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {allCodesGroups.filter(g => g.startsWith("[Custom] ")).map((g, idx) => (
                        <span key={idx} style={{ padding: "4px 12px", background: "#faf5ff", color: "#7e22ce", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "1px solid #d8b4fe" }}>{g.replace("[Custom] ", "")}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button onClick={() => setAllCodesOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
{/* ══ FORCE / HARD DELETE DIALOG ═══════════════════════════════════════ */}
{forceDialogOpen && (
  <div style={{ position:"fixed", inset:0, background:"#00000088", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)" }}>
    <div style={{ background:"#fff", borderRadius:16, padding:32, width:440, boxShadow:"0 20px 50px rgba(0,0,0,0.3)", border:"1px solid #fecaca" }}>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", gap:12 }}>
        <div style={{ width:60, height:60, borderRadius:"50%", background:"#fef2f2", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ fontSize:28, color:"#dc2626" }}></i>
        </div>

        {forceAction === "hardDelete" && (
          <>
            <h3 style={{ fontSize:18, fontWeight:700, color:"#1e293b", margin:0 }}>Permanent Delete</h3>
            <p style={{ fontSize:13, color:"#64748b", margin:0, lineHeight:1.6 }}>This will <strong style={{ color:"#dc2626" }}>permanently delete</strong> this record.<br />This action <strong>cannot be undone</strong>.</p>
            {forceError && (
              <div style={{ width:"100%", background:"#fef9c3", border:"1px solid #fde047", borderRadius:10, padding:"12px 16px", textAlign:"left" }}>
                <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                  <i className="fa-solid fa-circle-info" style={{ color:"#ca8a04", marginTop:2, flexShrink:0 }}></i>
                  <p style={{ fontSize:13, color:"#713f12", margin:0, lineHeight:1.6 }}>{forceError}</p>
                </div>
              </div>
            )}
            <div style={{ display:"flex", gap:10, width:"100%", marginTop:4 }}>
              <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }} style={{ flex:1, background:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0", borderRadius:9, padding:12, cursor:"pointer", fontWeight:600, fontSize:13 }}>Cancel</button>
              <button onClick={() => forceId !== null && permanentDeleteItem(forceId, true)} style={{ flex:1, background:"#7f1d1d", color:"#fff", border:"none", borderRadius:9, padding:12, cursor:"pointer", fontWeight:700, fontSize:13 }}>
                <i className="fa-solid fa-bolt" style={{ marginRight:6 }}></i>Force Delete Anyway
              </button>
            </div>
          </>
        )}

        {forceAction === "softDelete" && (
          <>
            <h3 style={{ fontSize:18, fontWeight:700, color:"#1e293b", margin:0 }}>Record Is In Use</h3>
            <div style={{ width:"100%", background:"#fef9c3", border:"1px solid #fde047", borderRadius:10, padding:"12px 16px", textAlign:"left" }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                <i className="fa-solid fa-circle-info" style={{ color:"#ca8a04", marginTop:2, flexShrink:0 }}></i>
                <p style={{ fontSize:13, color:"#713f12", margin:0, lineHeight:1.6 }}>{forceError}</p>
              </div>
            </div>
            <p style={{ fontSize:13, color:"#64748b", margin:0 }}>Do you want to <strong style={{ color:"#dc2626" }}>force delete</strong> anyway?</p>
            <div style={{ display:"flex", gap:10, width:"100%", marginTop:4 }}>
              <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }} style={{ flex:1, background:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0", borderRadius:9, padding:12, cursor:"pointer", fontWeight:600, fontSize:13 }}>Cancel</button>
              <button onClick={() => forceId !== null && forceSoftDelete(forceId)} style={{ flex:1, background:"#dc2626", color:"#fff", border:"none", borderRadius:9, padding:12, cursor:"pointer", fontWeight:700, fontSize:13 }}>
                <i className="fa-solid fa-bolt" style={{ marginRight:6 }}></i>Force Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
)}
      <Toaster />
    </div>
  );
};

export default ActivityAgeAndGender;
